import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { PasswordField } from '@components/core-ui/form-elements/PasswordField';
import Case from '@components/icons/Case.svg';
import EmailIcon from '@components/icons/Email.svg';
import UserIcon from '@components/icons/User.svg';
import { Spinner } from '@components/core-ui/spinners/Spinner';
import { accountTypes } from 'app/types/accountTypes';
import { normalizeEmail } from '@helpers/normalizeEmail';
import { PASSWORD_REQUIREMENTS_HINT } from '@helpers/validatePassword';
import { useAPI } from '@providers/useAPI';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { usePasswordFieldErrors } from 'app/hooks/usePasswordFieldErrors';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { checkDomain } from '../../../../api/checkDomain';
import { checkEmail } from '../../../../api/checkEmail';
import { getRequestErrorMessage } from '../../../../helpers/getRequestErrorMessage';
import { usePageContext } from '../../../../providers/PageProvider';
import { AgreementModal } from '../../components/AgreementModal';
import { StyledSelect } from '../../styles/StyledSelect';

const EMAIL_CHECK_DEBOUNCE_MS = 300;

export const RegistrationPageFirstStage = () => {
  const { call } = useAPI();
  const {
    setStage,
    isLoading,
    email,
    fromEmail,
    paramsEmail,
    name,
    setName,
    agreement,
    setAgreement,
    setEmail,
    password,
    setPassword,
    confirmation,
    setConfirmation,
    lastName,
    setLastName,
    accountType,
    setAccountType,
    company,
    setCompany,
    error,
    setError,
    goToLogin,
  } = usePageContext();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    passwordRef,
    confirmationRef,
    passwordError,
    confirmationError,
    onPasswordBlur,
    onConfirmationBlur,
    validatePasswordFields,
  } = usePasswordFieldErrors(password || '', confirmation || '');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState<boolean>(false);
  const [emailIsSSOError, setEmailIsSSOError] = useState<boolean>(false);
  const [isEmailCheckInvalid, setIsEmailCheckInvalid] = useState<boolean>(false);
  const [emailCheckError, setEmailCheckError] = useState<string>('');
  const isSubmitInFlight = useRef(false);
  const { mutateAsync: checkEmailAsTyped } = useMutation({
    mutationFn: (emailToCheck: string) => checkEmail({ call, requestBody: { email: emailToCheck } }),
  });
  const { mutateAsync: checkEmailOnSubmit } = useMutation({
    mutationFn: (emailToCheck: string) => checkEmail({ call, requestBody: { email: emailToCheck } }),
  });
  const { mutateAsync: checkSsoDomain } = useMutation({
    mutationFn: (emailToCheck: string) =>
      checkDomain({ call, requestBody: { domain: emailToCheck, email: emailToCheck } }),
  });

  const debouncedEmail = normalizeEmail(useDebouncedValue(email, EMAIL_CHECK_DEBOUNCE_MS));

  useEffect(() => {
    if (!debouncedEmail) {
      setIsEmailCheckInvalid(false);
      setEmailCheckError('');
      return;
    }
    let isCurrentEmail = true;
    checkEmailAsTyped(debouncedEmail)
      .then((emailCheckResult) => {
        if (!isCurrentEmail) {
          return;
        }
        if (!emailCheckResult?.valid) {
          setIsEmailCheckInvalid(true);
          setEmailCheckError(emailCheckResult.error ?? 'This email is already in use.');
          return;
        }
        setIsEmailCheckInvalid(false);
        setEmailCheckError('');
      })
      .catch((requestError: unknown) => {
        if (isCurrentEmail) {
          setIsEmailCheckInvalid(false);
          setEmailCheckError(getRequestErrorMessage(requestError));
        }
      });
    return () => {
      isCurrentEmail = false;
    };
  }, [debouncedEmail, checkEmailAsTyped]);

  const emailIsSSO = async () => {
    const result = await checkSsoDomain(normalizeEmail(email));

    if (result?.ssoRedirectUrl) {
      setError('err.ssoProvider');
      setEmailIsSSOError(true);
      setEmailError(true);
      return true;
    } else {
      setError('');
      setEmailIsSSOError(false);
    }

    return false;
  };

  const validateEmail = async () => {
    const isInternalStands =
      window.location.hostname === 'staging.autoscript.ai' || window.location.hostname === 'demo.autoscript.ai';

    const emailCheckResult = await checkEmailOnSubmit(normalizeEmail(email));
    if (!emailCheckResult?.valid) {
      setEmailError(true);
      setError(emailCheckResult.error ?? 'This email is already in use.');
      return false;
    }

    if (isInternalStands && !fromEmail) {
      const isEmailIncludesVoiceScript =
        normalizeEmail(email).endsWith('@voicescript.ai') || normalizeEmail(email).endsWith('@mailslurp.com');
      if (!isEmailIncludesVoiceScript) {
        setEmailError(true);
        setError('err.voicescriptEmail');
        return false;
      }
    }

    setEmailError(false);
    return true;
  };

  const handleFormSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (isSubmitInFlight.current) {
      return;
    }
    isSubmitInFlight.current = true;
    setError('');

    try {
      const emailIsSSOLogin = await emailIsSSO();
      const isEmailValid = await validateEmail();

      if (!isEmailValid || emailIsSSOLogin) {
        return;
      }

      if (!validatePasswordFields()) {
        return;
      }

      setStage('signupSurvey');
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError));
    } finally {
      isSubmitInFlight.current = false;
    }
  };

  const accountTypeOptions = Object.entries(accountTypes).map(([key, value]) => {
    return { value: key, label: value };
  });

  const isButtonDisabled =
    isLoading ||
    !name ||
    !lastName ||
    !normalizeEmail(email) ||
    !password ||
    !accountType ||
    !confirmation ||
    password !== confirmation ||
    !agreement ||
    emailIsSSOError;

  const displayedError = error || emailCheckError;

  return (
    <>
      {isLoading && <Spinner overlay={true} />}
      <div className="root">
        <h1 className="createNewAccount">Create a New Account</h1>
        <form onSubmit={handleFormSubmit}>
          <div className="margin20">
            <Input
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              autoComplete="given-name"
              name="name"
              label="First name"
              type="text"
              value={name || ''}
              required={true}
              icon={UserIcon}
            />
          </div>

          <div className="margin20">
            <Input
              onChange={(e) => {
                setLastName(e.target.value);
                setError('');
              }}
              autoComplete="family-name"
              name="lastName"
              label="Last name"
              type="text"
              value={lastName || ''}
              required={true}
              icon={UserIcon}
            />
          </div>

          <div className="margin20">
            <Input
              onChange={(e) => {
                setEmail(e.target.value.trim());
                setError('');
                setEmailError(false);
                setEmailIsSSOError(false);
              }}
              autoCapitalize="none"
              autoComplete="email"
              spellCheck={false}
              name="email"
              label="Email"
              type="email"
              value={email || ''}
              readOnly={!!(paramsEmail && email)}
              required={true}
              error={emailError || isEmailCheckInvalid}
              icon={EmailIcon}
            />
          </div>

          <div className="margin20">
            <PasswordField
              ref={passwordRef}
              name="password"
              label="Password"
              value={password || ''}
              onChange={(value) => {
                setPassword(value);
                setError('');
              }}
              onBlur={onPasswordBlur}
              isVisible={isPasswordVisible}
              onVisibilityToggle={() => setIsPasswordVisible(!isPasswordVisible)}
              hint={PASSWORD_REQUIREMENTS_HINT}
              error={passwordError}
              autoComplete="new-password"
              required={true}
            />
          </div>
          <div className="margin20">
            <PasswordField
              ref={confirmationRef}
              name="confirmation"
              label="Confirm password"
              value={confirmation || ''}
              onChange={(value) => {
                setConfirmation(value);
                setError('');
              }}
              onBlur={onConfirmationBlur}
              isVisible={isPasswordVisible}
              onVisibilityToggle={() => setIsPasswordVisible(!isPasswordVisible)}
              error={confirmationError}
              autoComplete="new-password"
              required={true}
            />
          </div>

          <div className="margin20">
            <StyledSelect
              name="accountType"
              label="Account Type"
              value={accountType ?? ''}
              onChange={(_name, value) => {
                setAccountType(value);
                setError('');
              }}
              options={accountTypeOptions}
              required={true}
              hideSelectedOption={true}
            />
          </div>

          <div className="margin20">
            <Input
              onChange={(e) => {
                setCompany(e.target.value);
                setError('');
              }}
              name="company"
              label="Company name"
              value={company || ''}
              icon={Case}
            />
          </div>

          <div className="agreement flex justify-center">
            <CheckBox
              name="accept-agreement"
              checked={agreement}
              onChange={(e) => {
                setAgreement(e);
              }}
              testId="accept-terms-checkbox"
            />
            <span className="text-left text-[12px] mb-[10px]">
              I agree to the &nbsp;
              <a href="#" onClick={() => setIsAgreementModalOpen(true)}>
                Terms of Service
              </a>{' '}
              &nbsp; and &nbsp;
              <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </span>
          </div>

          {displayedError ? (
            <div className="formError" data-testid="errorMessage">
              <ErrorMessage error={displayedError} />
            </div>
          ) : null}

          <p className="haveAnAccount">
            Have an account?&nbsp;&nbsp;
            <span onClick={goToLogin} className="signIn">
              Sign In
            </span>
          </p>

          <ButtonMedium disabled={isButtonDisabled} className="mb-[30px] mx-auto w-full" type="submit">
            Next
          </ButtonMedium>
        </form>
      </div>

      <AgreementModal isOpen={isAgreementModalOpen} setIsOpen={setIsAgreementModalOpen} />
    </>
  );
};
